/**
 * THE BLOG REGISTRY — one list, read by everything.
 *
 * `src/seo/routes.js` turns this into routes, which `scripts/prerender.js` and
 * `scripts/generateSitemap.js` both read, so a post added here is prerendered,
 * sitemapped and given its hreflang cluster in all three languages without
 * anybody remembering to do it. That is the whole point: the one defect this
 * site has produced twice is a page that exists and is invisible, because a
 * second hand-kept list did not know about it.
 *
 * A post's WORDS are not here. They live in `src/i18n/locales/{en,he,ar}.json`
 * under `blog.posts.<slug>`, so the same post exists in three languages by
 * construction and a missing translation is a test failure rather than an
 * English paragraph served under a Hebrew URL.
 *
 * `published: false` keeps a planned post out of the routes, the sitemap and
 * the index — visible here as the plan, invisible to a crawler until it has
 * words. Planning in the registry rather than in a document means the plan
 * cannot drift from what shipped.
 *
 * CommonJS, because routes.js and the build scripts are CommonJS and the
 * browser bundle imports it too — the same reason as seo/siteUrl.js.
 */

/** The four things a reader is ever looking for. */
const CATEGORIES = [
  { slug: 'grow', order: 1 },
  { slug: 'verticals', order: 2 },
  { slug: 'compare', order: 3 },
  { slug: 'capabilities', order: 4 },
];

/**
 * `date` is the publication date and becomes the post's `datePublished`. It is
 * also what orders the index, so it must be real: a date in the future would
 * sort a post above everything and tell an engine the page is not yet valid.
 */
const POSTS = [
  // ── Published ────────────────────────────────────────────────────────────
  {
    slug: 'all-in-one-vs-appointment-apps',
    category: 'compare',
    date: '2026-10-14',
    published: true,
    related: ['why-whatsapp-is-not-enough', 'run-a-property-on-one-system'],
  },
  {
    slug: 'run-a-property-on-one-system',
    category: 'verticals',
    date: '2026-10-14',
    published: true,
    related: ['all-in-one-vs-appointment-apps', 'deposits-and-the-empty-chair'],
  },
  {
    slug: 'deposits-and-the-empty-chair',
    category: 'verticals',
    date: '2026-10-14',
    published: true,
    related: ['why-whatsapp-is-not-enough', 'all-in-one-vs-appointment-apps'],
  },

  // ── Published 2026-10-15 ─────────────────────────────────────────────────
  {
    slug: 'why-whatsapp-is-not-enough',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['the-customers-you-already-have', 'a-page-people-trust'],
  },
  {
    slug: 'the-customers-you-already-have',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['what-reviews-are-really-for', 'what-your-calendar-is-hiding'],
  },
  {
    slug: 'a-page-people-trust',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['what-reviews-are-really-for', 'ads-that-pay-for-themselves'],
  },
  {
    slug: 'what-reviews-are-really-for',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['a-page-people-trust', 'the-customers-you-already-have'],
  },
  {
    slug: 'a-dental-practice-in-one-place',
    category: 'verticals',
    date: '2026-10-15',
    published: true,
    related: ['a-treatment-that-takes-three-visits', 'a-clinic-that-keeps-its-own-records'],
  },
  {
    slug: 'a-treatment-that-takes-three-visits',
    category: 'capabilities',
    date: '2026-10-15',
    published: true,
    related: ['two-chairs-one-diary', 'a-dental-practice-in-one-place'],
  },
  {
    slug: 'the-cost-of-a-no-show',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['deposits-and-the-empty-chair', 'getting-paid-before-they-arrive'],
  },
  {
    slug: 'selling-products-beside-the-chair',
    category: 'capabilities',
    date: '2026-10-15',
    published: true,
    related: ['the-desk-is-a-till-too', 'all-in-one-vs-appointment-apps'],
  },
  {
    slug: 'teaching-what-you-know',
    category: 'capabilities',
    date: '2026-10-15',
    published: true,
    related: ['selling-products-beside-the-chair', 'all-in-one-vs-appointment-apps'],
  },
  {
    slug: 'a-clinic-that-keeps-its-own-records',
    category: 'verticals',
    date: '2026-10-15',
    published: true,
    related: ['a-dental-practice-in-one-place', 'a-treatment-that-takes-three-visits'],
  },
  {
    slug: 'two-chairs-one-diary',
    category: 'capabilities',
    date: '2026-10-15',
    published: true,
    related: ['a-treatment-that-takes-three-visits', 'what-your-calendar-is-hiding'],
  },
  {
    slug: 'getting-paid-before-they-arrive',
    category: 'capabilities',
    date: '2026-10-15',
    published: true,
    related: ['deposits-and-the-empty-chair', 'the-cost-of-a-no-show'],
  },
  {
    slug: 'the-desk-is-a-till-too',
    category: 'capabilities',
    date: '2026-10-15',
    published: true,
    related: ['selling-products-beside-the-chair', 'what-your-calendar-is-hiding'],
  },
  {
    slug: 'ads-that-pay-for-themselves',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['a-page-people-trust', 'the-customers-you-already-have'],
  },
  {
    slug: 'a-barber-shop-at-full-capacity',
    category: 'verticals',
    date: '2026-10-15',
    published: true,
    related: ['the-cost-of-a-no-show', 'two-chairs-one-diary'],
  },
  {
    slug: 'what-your-calendar-is-hiding',
    category: 'grow',
    date: '2026-10-15',
    published: true,
    related: ['the-customers-you-already-have', 'the-cost-of-a-no-show'],
  },
  {
    slug: 'software-for-a-property-business',
    category: 'compare',
    date: '2026-10-15',
    published: true,
    related: ['all-in-one-vs-appointment-apps', 'run-a-property-on-one-system'],
  },
];

const publishedPosts = () => POSTS.filter((p) => p.published);

/** Newest first, which is how the index and every related strip reads. */
const postsByDate = () => [...publishedPosts()].sort((a, b) => (a.date < b.date ? 1 : -1));

const postsInCategory = (category) => postsByDate().filter((p) => p.category === category);

/** Only categories that have something to show — an empty page lists nobody. */
const activeCategories = () =>
  CATEGORIES.filter((c) => postsInCategory(c.slug).length > 0).sort((a, b) => a.order - b.order);

const postBySlug = (slug) => POSTS.find((p) => p.slug === slug && p.published);

/**
 * The posts shown at the foot of one post.
 *
 * A declared `related` wins, because an author knows better than a rule. It is
 * filtered to posts that are actually published, so planning a post and
 * linking to it early cannot produce a dead link. Anything short is topped up
 * from the same category, then from the newest — a reader who finishes a post
 * is always offered somewhere to go.
 */
function relatedPosts(slug, count = 3) {
  const post = postBySlug(slug);
  if (!post) return [];
  const out = [];
  const add = (p) => {
    if (p && p.slug !== slug && !out.some((x) => x.slug === p.slug)) out.push(p);
  };
  (post.related || []).forEach((s) => add(postBySlug(s)));
  postsInCategory(post.category).forEach(add);
  postsByDate().forEach(add);
  return out.slice(0, count);
}

module.exports = {
  CATEGORIES,
  POSTS,
  publishedPosts,
  postsByDate,
  postsInCategory,
  activeCategories,
  postBySlug,
  relatedPosts,
};
